import { UseFaq } from '@core/usecases/useFaq';
import { FormService, Logger } from '@core/services';
import { FaqCategoryEntity, FaqEntity } from '@core/entities';
import { ChangeDetectorRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@ngneat/dialog';
import { untilDestroyed } from '@ngneat/until-destroy';
import { FormBuilder, Validators } from '@angular/forms';
import { PERMISSIONS, PermissionService } from '@auth';
import { getAlertTypeByApprovedStatusOfEntity, GetTeamId, GetUserId, isMobileView, sortData } from '@core/utils';
import { instanceToPlain } from 'class-transformer';
import { HotToastService } from '@ngxpert/hot-toast';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { ConfirmationDialogType, DeleteConfirmationOptions, NotificationType, SocketEvents } from '@core/enums';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { saveAs } from 'file-saver';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';
import { CurrentSort } from '@core/types';

const log = new Logger('Faqs Composition');

export class FaqsComposition extends FormService {
  // For the Faqs Categories
  categories: FaqCategoryEntity[] = [];
  sortedCategories: FaqCategoryEntity[] = [];
  currentCategory: FaqCategoryEntity;
  currentSort: CurrentSort = { column: '', direction: '' };
  showImage: string[] = null;

  positions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  deleteSheetRef: any;
  deleteCategorySheetRef: any;
  // For the FAQs
  faqs: FaqEntity[] = [];
  currentFaq: FaqEntity;
  selectedCategoryId: number;
  tabs: { label: string; displayName: string }[] = [];
  selectedTab: string = null;

  selectedImage: File;

  loadings = {
    categoryList: true,
    faqList: true,
  };
  activeAccordion = -1;
  hideCategories = false;

  protected readonly _translateService = inject(TranslateService);
  protected readonly _dialogService = inject(DialogService);
  protected readonly _bottomSheetService = inject(NgxBottomSheetService);
  protected readonly _domSanitizer = inject(DomSanitizer);
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _useFaq = new UseFaq();
  private readonly _fb = inject(FormBuilder);
  private readonly _permissionService = inject(PermissionService);
  private readonly _toastService = inject(HotToastService);
  private readonly _router = inject(Router);
  private readonly _cdr = inject(ChangeDetectorRef);

  get canCreateFaq() {
    return (
      (this._permissionService.isAdmin() || this._permissionService.isFunctionary() || this._permissionService.isEditor() || this._permissionService.isSecretary()) &&
      this._permissionService.hasPermission(PERMISSIONS.CREATE_FAQ)
    );
  }

  get isAdmin(): boolean {
    return this._permissionService.isAdmin();
  }

  get isUserAuthor(): boolean {
    return this.currentFaq && +this.currentFaq.author === +GetUserId();
  }

  get alertType() {
    return getAlertTypeByApprovedStatusOfEntity(this.currentFaq);
  }

  onSortChange({ column, direction }: CurrentSort) {
    this.currentSort = { column, direction };
    this.sortedCategories = sortData(this.categories, column as keyof (typeof this.categories)[0], direction);
  }

  getIsFAQOwner(faq: FaqEntity) {
    return +faq.author === +GetUserId() || this._permissionService.isAdmin();
  }

  searchFaqByTitle(search: string) {
    this.loadings.faqList = true;
    this.loadings.categoryList = true;
    if (search) {
      this.hideCategories = true;
      this._searchFaq(search);
    } else {
      this.hideCategories = false;
      this._fetchAllFaqsCategories();
    }
  }

  setImageForPreview(imageUrl: string | SafeUrl) {
    if (imageUrl) {
      this.showImage = [imageUrl as string];
    } else {
      this.showImage = null;
    }
  }

  saveFaq(faq: FaqEntity, btnRef?: ButtonComponent) {
    if (!this.form.valid) return;

    faq = new FaqEntity(faq);
    faq.author = GetUserId();
    faq.approvedStatus = this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0;
    const body = this.removeNullObjects(instanceToPlain(faq));
    body.team_id = GetTeamId();
    const formData = this.jsonToFromData(body);

    if (this.selectedImage) {
      formData.append('file', this.selectedImage);
    }

    const service$ = this.currentFaq && this.currentFaq.id ? this._useFaq.updateFaq(this.currentFaq.id, formData) : this._useFaq.createFaq(formData);

    this.call(service$, btnRef)
      .then(() => {
        if (!this._permissionService.isAdmin()) {
          this._toastService.success(this._translateService.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
        } else {
          const message = this.currentFaq && this.currentFaq.id ? 'Update Successful' : 'Created Successfully';
          this._toastService.success(this._translateService.instant(message));
        }

        this._socketService.emit(SocketEvents.SendNotification, NotificationType.FAQ);

        if (isMobileView()) {
          this._bottomSheetService.closeAll();
        } else {
          this._dialogService.closeAll();
        }
      })
      .catch((err) => {
        log.error('Error creating FAQ', this.error);
        this._toastService.error(this.error);
      });
  }

  deleteFaq(faqId: number) {
    if (!faqId) return;
    const faq = this.faqs.find((f) => f.id === faqId);

    if (isMobileView()) {
      const sheetData = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('Faq'),
        },
      });
      sheetData.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (response: any) => {
          switch (response) {
            case DeleteConfirmationOptions.DELETE:
              if (response) {
                this._deleteFaqById(faqId);
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data: {
            type: ConfirmationDialogType.DELETE,
            emphasize: faq.title,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (result: any) => {
            if (result) {
              this._deleteFaqById(faqId);
            }
          },
        });
    }
  }

  saveCategory(category: FaqCategoryEntity, btnRef?: ButtonComponent) {
    if (!this.form.valid) return;

    category = new FaqCategoryEntity(category);
    category.author = GetUserId();
    category.approvedStatus = this._permissionService.isAdmin() || this._permissionService.isFunctionary() ? 1 : 0;
    const body = this.removeNullObjects(instanceToPlain(category));

    const service$ = this.currentCategory && this.currentCategory.id ? this._useFaq.updateFaqCategory(this.currentCategory.id, body) : this._useFaq.createFaqCategory(body);

    this.call(service$, btnRef)
      .then(() => {
        const message = this.currentCategory && this.currentCategory.id ? 'Update Successful' : 'Created Successfully';
        this._toastService.success(this._translateService.instant(message));
        this._socketService.emit(SocketEvents.SendNotification, NotificationType.FAQ);
        // this._fetchAllFaqsCategories();
        if (isMobileView()) {
          this._bottomSheetService.close();
        } else {
          this._dialogService.closeAll();
        }
        this._reloadCurrentRoute();
      })
      .catch((err) => {
        log.error('Error creating Faq Category', this.error);
        this._toastService.error(this.error);
      });
  }

  deleteCategory(category: FaqCategoryEntity) {
    if (!category.id) return;

    if (isMobileView()) {
      const categorySheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('Faq Category'),
        },
      });
      categorySheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res: any) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.deleteCategoryById(category);
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          size: 'md',
          closeButton: false,
          windowClass: 'dialog-overflow-unset',
          data: {
            type: ConfirmationDialogType.DELETE,
            emphasize: category.title,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (result: any) => {
            if (result) {
              this.deleteCategoryById(category);
            }
          },
        });
    }
  }

  deleteCategoryById(categoryId: FaqCategoryEntity) {
    this.call(this._useFaq.deleteFaqCategory(categoryId.id))
      .then(() => {
        this._toastService.success(this._translateService.instant('Delete Successful'));
        this._socketService.emit(SocketEvents.SendNotification, NotificationType.FAQ);
        this._reloadCurrentRoute();
      })
      .catch((err) => {
        log.error('Error deleting Faq Category', this.error);
        this._toastService.error(this.error);
      });
  }

  async downloadImage(faq: FaqEntity, btnRef?: ButtonComponent) {
    if (faq.image && faq.title) {
      btnRef?.showLoading();

      try {
        // Fetch the image
        const response = await fetch(faq?.imageUrl as string);
        if (!response.ok) throw new Error('Network response was not ok.');
        const blob = await response.blob();

        // Create a Blob URL
        const blobUrl = URL.createObjectURL(blob);

        // Use the title for the filename, ensuring it ends with '.jpg'
        const filename = `faq-image-${faq.title.replace(/\s+/g, '-').toLowerCase()}.jpg`;

        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up by revoking the Blob URL
        URL.revokeObjectURL(blobUrl);

        btnRef?.showIdle();
      } catch (error) {
        log.error('Error downloading the image:', error);
        btnRef?.showError();
      }
    } else {
      log.error('FAQ image URL or title is missing.');
      btnRef?.showError();
    }
  }

  async downloadDocument(faq: FaqEntity, btnRef?: ButtonComponent) {
    if (faq.document && faq.title) {
      btnRef?.showLoading();

      try {
        const filePath = faq.document;
        const fileName = filePath.split('/').pop();

        const response = await this._useFaq.downloadFaqDocument(filePath, faq.id).toPromise();
        const blob = new Blob([response], { type: 'application/octet-stream' }); // Continue using a generic MIME type

        // Use FileSaver to save the file
        saveAs(blob, fileName || 'faq-document');

        btnRef?.showIdle();
      } catch (error) {
        log.error('Error downloading the document:', error);
        btnRef?.showError();
      }
    } else {
      log.error('FAQ document URL or title is missing.');
      btnRef?.showError();
    }
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchFaqById(faqId: number): Observable<FaqEntity> {
    return this._useFaq.getFaqById(faqId).pipe(
      untilDestroyed(this),
      finalize(() => (this.isLoading = false)),
    );
  }

  protected _initCategoryForm() {
    this.form = this._fb.group({
      title: [null, [Validators.required]],
      position: [null, [Validators.required]],
    });
  }

  protected _initFaqForm() {
    this.form = this._fb.group({
      title: [null, [Validators.required]],
      category: [null, [Validators.required]],
      position: [null, [Validators.required]],
      description: [null, [Validators.required]],
      image: [null],
    });
  }

  protected _fetchAllFaqsCategories(fetchFqsForFirstCategory = false) {
    this.loadings.categoryList = true;
    this.categories = [];
    this._useFaq
      .getFaqCategories()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.categories = res;
          this.sortedCategories = this.categories;
          this._cdr.markForCheck();
          if (this.categories.length > 0) {
            this._transformCategoriesToTabs();
          } else {
            this.loadings.categoryList = false;
            this.loadings.faqList = false;
          }
        },
      });
  }

  protected _fetchFAQSbyCategory(categoryId: number) {
    this.loadings.faqList = true;
    this._useFaq
      .getFaqsByCategory(categoryId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          // sort by position
          this.faqs = res.sort((a, b) => a.position - b.position);
          this.activeAccordion = -1;
          this.loadings.faqList = false;
        },
      });
  }

  protected _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }

  // Delete FAQ Common Function for WEB and MOBILE
  private _deleteFaqById(faqId: number) {
    this.call(this._useFaq.deleteFaq(faqId))
      .then(() => {
        this._toastService.success(this._translateService.instant('Delete Successful'));
        this._socketService.emit(SocketEvents.SendNotification, NotificationType.FAQ);
        if (isMobileView()) {
          this._bottomSheetService.close();
        } else {
          this._dialogService.closeAll();
        }
        // this._reloadCurrentRoute();
      })
      .catch((err) => {
        log.error('Error deleting FAQ', this.error);
        this._toastService.error(this.error);
      });
  }

  private _transformCategoriesToTabs() {
    this.tabs = this.categories.map((category) => {
      return {
        label: category.id.toString(),
        displayName: category.title,
      };
    });
    this.selectedTab = this.tabs[0]?.label;
    if (isMobileView()) this._fetchFAQSbyCategory(+this.selectedTab);
    this.loadings.categoryList = false;
  }

  private _searchFaq(query: string) {
    this._useFaq
      .findFaqByTitle(query)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.faqs = res;
          this.loadings.faqList = false;
        },
      });
  }
}
